import React from 'react';
import Text from '../common/Text';
import Button from '../common/Button';
import { AssessmentType } from '../../api/services/assessment';

interface AssessmentTypeCardProps {
  type: AssessmentType;
  title: string;
  duration: string;
  description: string;
  features: string[];
  color: 'blue' | 'green';
  isComingSoon?: boolean;
  onStart: () => void;
  isStarting: boolean;
}

const AssessmentTypeCard: React.FC<AssessmentTypeCardProps> = ({
  type,
  title,
  duration,
  description,
  features,
  color,
  isComingSoon = false,
  onStart,
  isStarting
}) => {
  const colorClasses = {
    blue: {
      text: 'text-blue-600 dark:text-blue-400',
      border: 'hover:border-blue-500',
      badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      icon: 'text-blue-500'
    },
    green: {
      text: 'text-green-600 dark:text-green-400',
      border: 'hover:border-green-500',
      badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
      icon: 'text-green-500'
    }
  };

  const colors = colorClasses[color];

  return (
    <div className={`border rounded-xl p-8 ${colors.border} transition-colors relative bg-white dark:bg-gray-800 shadow-sm hover:shadow-md`}>
      <div className="flex items-center justify-between mb-6">
        <Text variant="h3" className={`${colors.text} text-2xl font-semibold`}>{title}</Text>
        <div className="flex items-center">
          <div className={`${colors.badge} text-sm px-3 py-1.5 rounded-full font-medium text-center`}>
            {duration}
          </div>
        </div>
      </div>
      <Text variant="p" className="text-gray-600 dark:text-gray-400 mb-6 text-lg leading-relaxed">
        {description}
      </Text>
      <div className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <div key={index} className="flex items-center text-base text-gray-600 dark:text-gray-400">
            <svg className={`w-5 h-5 mr-3 ${colors.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </div>
        ))}
      </div>
      <Button
        variant={isComingSoon ? "secondary" : "primary"}
        className={`w-full py-3 text-lg ${isComingSoon ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={onStart}
        disabled={isStarting || isComingSoon}
      >
        {isStarting ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Starting...
          </div>
        ) : (
          isComingSoon ? "Coming Soon" : `Start ${title}`
        )}
      </Button>
    </div>
  );
};

export default AssessmentTypeCard;